import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';

import {
  MoodleDozentAccess,
  MoodleDozentCandidate,
  TutorService,
} from '../../services/tutor.service';

/**
 * Picks which Moodle lecturers a Twin admits to its Moodle functions.
 *
 * The list lives in `bot_moodle_dozent_access`, not in `template_settings`,
 * so the generic settings pipeline cannot render it — same reason as the
 * dozent-link picker next door.
 *
 * Two things the component states rather than hides, because both change
 * what the operator sees:
 *
 *  - Moodle site admins are not in synced data. They hold their rights at
 *    system level and are enrolled in no course, so no sync can find them.
 *  - Without Moodle PII sync the mirror stores no names, and the only thing
 *    left to identify a person by is their numeric Moodle id.
 */
@Component({
  selector: 'app-moodle-dozent-access',
  templateUrl: './moodle-dozent-access.component.html',
  styleUrl: './moodle-dozent-access.component.scss',
})
export class MoodleDozentAccessComponent implements OnChanges {
  @Input() dozentBotId!: string;
  /** The Twin's saved audience. The picker only matters while it is
   *  `selected`; for `all_teachers` it renders as a read-only preview. */
  @Input() audience: string = 'all_teachers';

  access: MoodleDozentAccess | null = null;
  loading = false;
  saving = false;
  error = '';
  search = '';

  private selected = new Set<string>();

  constructor(
    private tutorService: TutorService,
    private toastr: NbToastrService,
    private translate: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dozentBotId'] && this.dozentBotId) {
      this.load();
    }
  }

  get curated(): boolean {
    return this.audience === 'selected';
  }

  get candidates(): MoodleDozentCandidate[] {
    const all = this.access?.candidates ?? [];
    const term = this.search.trim().toLowerCase();
    if (!term) return all;
    return all.filter((c) => {
      const haystack = [
        c.first_name,
        c.last_name,
        c.email,
        String(c.moodle_user_id),
        ...c.courses.map((course) => course.name ?? ''),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }

  get selectedCount(): number {
    return this.selected.size;
  }

  get inScopeCount(): number {
    return (this.access?.candidates ?? []).filter((c) => c.in_scope).length;
  }

  displayName(c: MoodleDozentCandidate): string {
    const full = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
    return full || c.email || `Moodle #${c.moodle_user_id}`;
  }

  courseSummary(c: MoodleDozentCandidate): string {
    const named = c.courses
      .filter((course) => course.in_scope)
      .map((course) => course.name)
      .filter(Boolean) as string[];
    if (named.length) {
      return named.slice(0, 3).join(', ') + (named.length > 3 ? ' …' : '');
    }
    return this.translate.instant('tutor.moodleDozentAccess.noScopedCourse');
  }

  isSelected(c: MoodleDozentCandidate): boolean {
    return this.selected.has(c.moodle_user_uuid);
  }

  /** Whether this person has the Twin's Moodle functions right now.
   *
   *  In `all_teachers` the curated list is not consulted at all — everyone
   *  teaching a course of this Twin is admitted. Rendering their boxes empty
   *  (the saved selection) showed the opposite of what is true and made the
   *  disabled list read as broken. */
  hasAccess(c: MoodleDozentCandidate): boolean {
    return this.curated ? this.isSelected(c) : c.in_scope;
  }

  toggle(c: MoodleDozentCandidate, on: boolean): void {
    if (on) {
      this.selected.add(c.moodle_user_uuid);
    } else {
      this.selected.delete(c.moodle_user_uuid);
    }
  }

  selectAllInScope(): void {
    for (const c of this.access?.candidates ?? []) {
      if (c.in_scope) this.selected.add(c.moodle_user_uuid);
    }
  }

  clearSelection(): void {
    this.selected.clear();
  }

  save(): void {
    if (this.saving || !this.dozentBotId) return;
    this.saving = true;
    this.error = '';
    this.tutorService
      .setMoodleDozentAccess(this.dozentBotId, [...this.selected])
      .subscribe({
        next: (res) => {
          this.selected = new Set(res.selected);
          this.saving = false;
          this.toastr.success(
            this.translate.instant('tutor.moodleDozentAccess.saved'),
            this.translate.instant('common.success')
          );
        },
        error: () => {
          this.saving = false;
          this.error = this.translate.instant('tutor.moodleDozentAccess.saveFailed');
        },
      });
  }

  private load(): void {
    this.loading = true;
    this.error = '';
    this.tutorService.getMoodleDozentAccess(this.dozentBotId).subscribe({
      next: (data) => {
        this.access = data;
        this.selected = new Set(data.selected);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.error = this.translate.instant('tutor.moodleDozentAccess.loadFailed');
      },
    });
  }
}
